import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Grid,
  Typography,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Button,
  Stack,
  InputAdornment,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  FileCopy as FileCopyIcon,
} from '@mui/icons-material';
import { MediaItem } from '../../services/contentManagement';

interface Props {
  media: MediaItem[];
  onSelect: (item: MediaItem) => void;
  onAdd: (file: File) => void;
  onDelete: (id: string) => void;
}

const MediaLibrary: React.FC<Props> = ({
  media,
  onSelect,
  onAdd,
  onDelete,
}) => {
  const [search, setSearch] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newMediaTitle, setNewMediaTitle] = useState('');
  const [newMediaTags, setNewMediaTags] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setSelectedFile(event.target.files[0]);
    }
  };

  const handleAddMedia = () => {
    if (selectedFile) {
      onAdd(selectedFile);
      setAddDialogOpen(false);
      setSelectedFile(null);
      setNewMediaTitle('');
      setNewMediaTags('');
    }
  };

  const filteredMedia = media.filter((item) => {
    const matchesSearch = item.title.toLowerCase().includes(search.toLowerCase()) ||
      item.tags.some(tag => tag.toLowerCase().includes(search.toLowerCase()));
    const matchesTags = selectedTags.length === 0 ||
      selectedTags.every(tag => item.tags.includes(tag));
    return matchesSearch && matchesTags;
  });

  const allTags = Array.from(new Set(media.flatMap(item => item.tags)));

  return (
    <>
      <Box sx={{ mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs>
            <TextField
              fullWidth
              placeholder="Buscar mídia..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setAddDialogOpen(true)}
            >
              Adicionar Mídia
            </Button>
          </Grid>
        </Grid>

        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Filtrar por tags:
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {allTags.map((tag) => (
              <Chip
                key={tag}
                label={tag}
                onClick={() => {
                  if (selectedTags.includes(tag)) {
                    setSelectedTags(selectedTags.filter(t => t !== tag));
                  } else {
                    setSelectedTags([...selectedTags, tag]);
                  }
                }}
                color={selectedTags.includes(tag) ? "primary" : "default"}
              />
            ))}
          </Stack>
        </Box>
      </Box>

      <Grid container spacing={2}>
        {filteredMedia.map((item) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={item.id}>
            <Card>
              <CardMedia
                component={item.type === 'video' ? 'video' : 'img'}
                image={item.url}
                sx={{ height: 200, cursor: 'pointer' }}
                onClick={() => onSelect(item)}
              />
              <CardContent>
                <Typography variant="subtitle1" noWrap>
                  {item.title}
                </Typography>
                <Box sx={{ mt: 1 }}>
                  {item.tags.map((tag) => (
                    <Chip
                      key={tag}
                      label={tag}
                      size="small"
                      sx={{ mr: 0.5, mb: 0.5 }}
                    />
                  ))}
                </Box>
                <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between' }}>
                  <IconButton size="small" onClick={() => onSelect(item)}>
                    <FileCopyIcon />
                  </IconButton>
                  <IconButton size="small" onClick={() => onDelete(item.id)}>
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)}>
        <DialogTitle>Adicionar Nova Mídia</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Button
              variant="outlined"
              component="label"
              fullWidth
              sx={{ mb: 2 }}
            >
              Selecionar Arquivo
              <input
                type="file"
                hidden
                accept="image/*,video/*"
                onChange={handleFileSelect}
              />
            </Button>
            {selectedFile && (
              <Typography variant="body2" sx={{ mb: 2 }}>
                Arquivo selecionado: {selectedFile.name}
              </Typography>
            )}
            <TextField
              fullWidth
              label="Título"
              value={newMediaTitle}
              onChange={(e) => setNewMediaTitle(e.target.value)}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Tags (separadas por vírgula)"
              value={newMediaTags}
              onChange={(e) => setNewMediaTags(e.target.value)}
              sx={{ mb: 2 }}
            />
            <Button
              fullWidth
              variant="contained"
              onClick={handleAddMedia}
              disabled={!selectedFile || !newMediaTitle}
            >
              Adicionar
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default MediaLibrary;
